import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Check, Clapperboard, ListMusic, Search, Sparkles } from "lucide-react";
import { IDEA_FACT_FILTERS, type IdeaFactFilter } from "#shared/ideaScoreWeights.ts";
import type { IdeaCandidate, IdeaDisplayFact, IdeaDisplaySlot } from "#shared/schemas/idea.ts";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { QueryErrorState } from "@web/features/diagnostics/QueryErrorState.tsx";
import {
  useLocalPlaylists,
  usePlaylists,
} from "@web/features/library/api.ts";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { useEnsureIdea, useIdeaCandidates } from "@web/features/ideas/ideasApi.ts";
import { useGlobeData } from "@web/features/globe/api.ts";
import {
  artistIdsFromIdeaCandidates,
  enqueueShotArtists,
} from "@web/features/globe/enqueueShotArtists.ts";
import { useBrowseTrailStore } from "@web/stores/browseTrail.ts";
import { useUiStore } from "@web/stores/ui.ts";
import { cn } from "@web/lib/utils.ts";

const FACT_LABELS: Record<IdeaFactFilter, string> = {
  alias: "Has born-as",
  annotation: "Has annotation",
  place: "Has recording place",
  reissues: "Has reissues",
  posthumous: "Posthumous",
  age: "Age at recording",
  collaboration: "Collaboration",
  defunct_label: "Defunct label",
  samples: "Samples",
  country: "Has country",
  years: "Has years",
};

const FACT_SLOTS: IdeaDisplaySlot[] = ["born", "place", "age", "spotlight"];

/** Shared grid so fact columns line up across every card. */
const FACT_GRID =
  "grid grid-cols-[minmax(0,1.35fr)_minmax(0,1.35fr)_minmax(0,0.55fr)_minmax(0,1.1fr)] gap-x-4";

function ideaCardMatchesTrail(
  candidate: IdeaCandidate,
  trailRoot: { type: string; id: string } | null,
): boolean {
  if (!trailRoot) return false;
  if (trailRoot.type === candidate.entity_type && trailRoot.id === candidate.entity_id) {
    return true;
  }
  // Artist opened from the card’s credit line — keep the track row lit.
  if (trailRoot.type === "artist") {
    return candidate.artists.some((a) => a.id === trailRoot.id);
  }
  return false;
}

function factBySlot(
  facts: readonly IdeaDisplayFact[],
  slot: IdeaDisplaySlot,
): IdeaDisplayFact | null {
  return facts.find((f) => f.slot === slot) ?? null;
}

function IdeaCard({
  candidate,
  active,
  onInspect,
  onCompose,
  onOpenSamples,
  onOpenArtist,
  busy,
}: {
  candidate: IdeaCandidate;
  /** Sheet is open on this card’s entity (or dive that started here). */
  active: boolean;
  onInspect: () => void;
  onCompose: () => void;
  onOpenSamples: () => void;
  onOpenArtist: (id: string, name: string) => void;
  busy: boolean;
}) {
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [annotationOpen, setAnnotationOpen] = useState(false);
  const namedArtists = candidate.artists.filter((a) => a.name);
  const samplesSpotlight = (candidate.display_facts ?? []).find(
    (f) => f.slot === "spotlight" && /sample/i.test(f.text),
  );
  const annotationSpotlight = (candidate.display_facts ?? []).find(
    (f) => f.kind === "annotation",
  );

  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  function clearClickTimer() {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
  }

  function onClick() {
    clearClickTimer();
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      onInspect();
    }, 250);
  }

  function onDoubleClick(e: React.MouseEvent) {
    e.preventDefault();
    clearClickTimer();
    onCompose();
  }

  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      aria-current={active ? "true" : undefined}
      title="Click to inspect · double-click to compose"
      className={cn(
        "flex w-full gap-4 px-1 py-4 text-left transition-colors",
        "hover:bg-row-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent",
        active && "bg-row-active hover:bg-row-active",
        candidate.used && "opacity-55",
      )}
    >
      <EntityAvatar src={candidate.cover_url} alt="" size="lg" rounded="sm" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-lg font-semibold tracking-tight text-text">
              {candidate.name}
            </div>
            <div className="mt-0.5 truncate text-sm text-muted">
              {namedArtists.length > 0 ? (
                <>
                  {namedArtists.map((a, i) => (
                    <span key={a.id}>
                      {i > 0 && ", "}
                      <span
                        role="link"
                        tabIndex={0}
                        className="hover:text-text hover:underline"
                        title={a.name ?? undefined}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          clearClickTimer();
                          onOpenArtist(a.id, a.name ?? "Artist");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            clearClickTimer();
                            onOpenArtist(a.id, a.name ?? "Artist");
                          }
                        }}
                        onDoubleClick={(e) => e.stopPropagation()}
                      >
                        {a.name}
                      </span>
                    </span>
                  ))}
                  {candidate.year != null && (
                    <span className="text-muted">{` · ${candidate.year}`}</span>
                  )}
                </>
              ) : (
                candidate.year ?? "—"
              )}
            </div>
          </div>
          {candidate.used && (
            <span className="inline-flex shrink-0 items-center gap-1 pt-1 text-xs text-faint">
              <Check className="size-3.5" aria-hidden />
              Used
            </span>
          )}
          {candidate.researched ? (
            <span className="inline-flex shrink-0 items-center gap-1 pt-1 text-xs font-semibold text-research">
              <Sparkles className="size-3.5" aria-hidden />
              Researched
            </span>
          ) : candidate.has_cards ? (
            <span className="inline-flex shrink-0 items-center gap-1 pt-1 text-xs font-semibold text-accent">
              <ListMusic className="size-3.5" aria-hidden />
              Cards
            </span>
          ) : null}
        </div>

        <div className={cn(FACT_GRID, "text-sm text-muted")} aria-label={candidate.reason}>
          {FACT_SLOTS.map((slot) => {
            const fact = factBySlot(candidate.display_facts ?? [], slot);
            const text = fact?.text ?? null;
            const isSamples = slot === "spotlight" && Boolean(samplesSpotlight);
            const isAnnotation =
              slot === "spotlight" && fact?.kind === "annotation" && Boolean(fact.body);

            if (isSamples && text) {
              return (
                <span
                  key={slot}
                  role="link"
                  tabIndex={0}
                  className="min-h-5 truncate text-accent hover:underline"
                  title="Open samples in sheet"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    clearClickTimer();
                    onOpenSamples();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      clearClickTimer();
                      onOpenSamples();
                    }
                  }}
                >
                  {text}
                </span>
              );
            }

            if (isAnnotation && text) {
              return (
                <span
                  key={slot}
                  role="button"
                  tabIndex={0}
                  aria-expanded={annotationOpen}
                  className="min-h-5 truncate hover:text-text"
                  title={annotationOpen ? "Collapse annotation" : "Expand annotation"}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    clearClickTimer();
                    setAnnotationOpen((open) => !open);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      clearClickTimer();
                      setAnnotationOpen((open) => !open);
                    }
                  }}
                  onDoubleClick={(e) => e.stopPropagation()}
                >
                  <span className="text-faint">Annotation · </span>
                  {text}
                </span>
              );
            }

            return (
              <span key={slot} className="min-h-5 truncate" title={text ?? undefined}>
                {text ?? ""}
              </span>
            );
          })}
        </div>

        {annotationOpen && annotationSpotlight?.body && (
          <div
            className="rounded-md border border-line bg-panel-elevated px-3 py-2 text-sm leading-relaxed text-muted whitespace-pre-wrap"
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {annotationSpotlight.body}
          </div>
        )}
      </div>
    </button>
  );
}

export function IdeasPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get("q") ?? "";
  const usedParam = (searchParams.get("used") as "unused" | "used" | "all" | null) ?? "unused";
  const cardsParam = (searchParams.get("cards") as "with" | "without" | "all" | null) ?? "all";
  const factParam = searchParams.get("fact") as IdeaFactFilter | null;
  const playlistId = searchParams.get("playlist") ?? undefined;
  const localPlaylistId = searchParams.get("local") ?? undefined;

  const [draftQ, setDraftQ] = useState(qParam);
  const playlists = usePlaylists();
  const local = useLocalPlaylists();
  const ensure = useEnsureIdea();
  const openDetail = useOpenDetail();
  const sheetOpen = useUiStore((s) => s.sheetOpen);
  const trailRoot = useBrowseTrailStore((s) => s.stack[0] ?? null);

  useEffect(() => {
    setDraftQ(qParam);
  }, [qParam]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (draftQ.trim()) next.set("q", draftQ.trim());
      else next.delete("q");
      if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    }, 200);
    return () => window.clearTimeout(handle);
  }, [draftQ]); // eslint-disable-line react-hooks/exhaustive-deps

  const fact =
    factParam && (IDEA_FACT_FILTERS as readonly string[]).includes(factParam)
      ? factParam
      : undefined;
  const used =
    usedParam === "used" || usedParam === "all" || usedParam === "unused"
      ? usedParam
      : "unused";
  const cards =
    cardsParam === "with" || cardsParam === "without" || cardsParam === "all"
      ? cardsParam
      : "all";

  const candidates = useIdeaCandidates({
    q: qParam || undefined,
    playlist_id: playlistId,
    local_playlist_id: localPlaylistId,
    fact,
    used,
    cards,
    limit: 120,
  });
  const globe = useGlobeData();

  const playlistOptions = useMemo(() => {
    const spotify = (playlists.data ?? []).map((p) => ({
      key: `p:${p.id}`,
      label: p.name,
      kind: "playlist" as const,
      id: p.id,
    }));
    const locals = (local.data ?? []).map((p) => ({
      key: `l:${p.id}`,
      label: p.name,
      kind: "local" as const,
      id: p.id,
    }));
    return [...spotify, ...locals];
  }, [playlists.data, local.data]);

  function patchParams(mutate: (next: URLSearchParams) => void) {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 flex-col gap-3 border-b border-line px-8 py-5">
        <div className="mx-auto w-full max-w-ideas min-w-0">
          <p className="text-xs font-semibold tracking-wider text-faint uppercase">Ideas</p>
          <h1 className="text-2xl font-semibold tracking-tight">Ideas</h1>
          <p className="mt-1 text-sm text-muted">
            {candidates.isLoading
              ? "Loading…"
              : `${(candidates.data?.length ?? 0).toLocaleString()} reel-worthy subjects`}
            <span className="text-faint"> · click inspects · double-click composes</span>
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <label className="relative min-w-48 flex-1 basis-48">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-faint" />
              <input
                type="search"
                value={draftQ}
                onChange={(e) => setDraftQ(e.target.value)}
                placeholder="Search by name or artist"
                className={cn(
                  "h-9 w-full rounded-full bg-panel-elevated pr-4 pl-10 text-sm text-text",
                  "placeholder:text-faint focus-visible:outline-none",
                )}
              />
            </label>

            <select
              aria-label="Playlist scope"
              className="h-8 max-w-48 rounded-md border border-line bg-panel px-2 text-sm text-text"
              value={
                playlistId
                  ? `p:${playlistId}`
                  : localPlaylistId
                    ? `l:${localPlaylistId}`
                    : ""
              }
              onChange={(e) => {
                const v = e.target.value;
                patchParams((next) => {
                  next.delete("playlist");
                  next.delete("local");
                  if (v.startsWith("p:")) next.set("playlist", v.slice(2));
                  else if (v.startsWith("l:")) next.set("local", v.slice(2));
                });
              }}
            >
              <option value="">All playlists</option>
              {playlistOptions.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.kind === "local" ? `Local · ${p.label}` : p.label}
                </option>
              ))}
            </select>

            <select
              aria-label="Fact type"
              className="h-8 max-w-48 rounded-md border border-line bg-panel px-2 text-sm text-text"
              value={fact ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                patchParams((next) => {
                  if (v) next.set("fact", v);
                  else next.delete("fact");
                });
              }}
            >
              <option value="">Any fact</option>
              {IDEA_FACT_FILTERS.map((key) => (
                <option key={key} value={key}>
                  {FACT_LABELS[key]}
                </option>
              ))}
            </select>

            <select
              aria-label="Used filter"
              className="h-8 rounded-md border border-line bg-panel px-2 text-sm text-text"
              value={used}
              onChange={(e) => {
                const v = e.target.value as "unused" | "used" | "all";
                patchParams((next) => {
                  if (v === "unused") next.delete("used");
                  else next.set("used", v);
                });
              }}
            >
              <option value="unused">Unused</option>
              <option value="used">Used</option>
              <option value="all">All</option>
            </select>

            <select
              aria-label="Cards filter"
              className="h-8 rounded-md border border-line bg-panel px-2 text-sm text-text"
              value={cards}
              onChange={(e) => {
                const v = e.target.value as "with" | "without" | "all";
                patchParams((next) => {
                  if (v === "all") next.delete("cards");
                  else next.set("cards", v);
                });
              }}
            >
              <option value="all">Any cards</option>
              <option value="with">Has cards</option>
              <option value="without">No cards</option>
            </select>

            <button
              type="button"
              disabled={!candidates.data?.length || !globe.data}
              className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-md border border-line px-2.5 text-xs font-semibold",
                "text-muted hover:border-accent/40 hover:text-text disabled:opacity-40",
              )}
              onClick={() => {
                if (!candidates.data || !globe.data) return;
                const { ids, names } = artistIdsFromIdeaCandidates(candidates.data);
                enqueueShotArtists(ids, names, globe.data.points);
              }}
            >
              <Clapperboard className="size-3.5" />
              Add to shot queue
            </button>
            <Link
              to="/globe"
              className="inline-flex h-8 items-center rounded-md px-2 text-xs font-semibold text-faint hover:text-text"
            >
              Open globe
            </Link>
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-2">
        <div className="mx-auto w-full max-w-ideas">
          {candidates.isLoading && (
            <div className="space-y-3 py-4">
              {Array.from({ length: 8 }, (_, i) => (
                <Skeleton key={i} className="h-24 w-full rounded-md" />
              ))}
            </div>
          )}

          {candidates.isError && (
            <QueryErrorState
              error={candidates.error}
              onRetry={() => void candidates.refetch()}
              actionLabel="load ideas"
            />
          )}

          {candidates.isSuccess && (candidates.data?.length ?? 0) === 0 && (
            <EmptyState
              title="Nothing surfaced yet"
              description={
                qParam || fact || playlistId || localPlaylistId || used !== "unused"
                  ? "Try clearing a filter, or start an idea from any entity sheet."
                  : "Enrich tracks with MusicBrainz, or use Start idea on a sheet for something the ranking missed."
              }
            />
          )}

          {candidates.isSuccess && (candidates.data?.length ?? 0) > 0 && (
            <div className="divide-y divide-line">
              {/* Column hints — faint, no table chrome */}
              <div
                className={cn(
                  "hidden items-center gap-4 px-1 py-2 sm:flex",
                  "text-xs font-semibold tracking-wider text-faint uppercase",
                )}
                aria-hidden
              >
                <div className="size-12 shrink-0" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-7" />
                  <div className={FACT_GRID}>
                    <span>Born as</span>
                    <span>Studio</span>
                    <span>Age</span>
                    <span>Standout</span>
                  </div>
                </div>
              </div>

              {(() => {
                const all = candidates.data ?? [];
                const filled = all.filter((c) => c.has_cards);
                const rest = all.filter((c) => !c.has_cards);
                return (
                  <>
                    {filled.length > 0 && (
                      <div className="pt-2">
                        <p className="px-1 pb-1 text-xs font-semibold tracking-wider text-research uppercase">
                          With cards
                        </p>
                        {filled.map((c) => (
                          <IdeaCard
                            key={`f:${c.entity_type}:${c.entity_id}`}
                            candidate={c}
                            active={sheetOpen && ideaCardMatchesTrail(c, trailRoot)}
                            busy={ensure.isPending}
                            onInspect={() =>
                              openDetail(
                                {
                                  type: c.entity_type,
                                  id: c.entity_id,
                                  label: c.name,
                                },
                                { mode: "reset" },
                              )
                            }
                            onOpenSamples={() =>
                              openDetail(
                                {
                                  type: c.entity_type,
                                  id: c.entity_id,
                                  label: c.name,
                                },
                                { mode: "reset", sectionFocus: "samples" },
                              )
                            }
                            onOpenArtist={(id, name) =>
                              openDetail(
                                { type: "artist", id, label: name },
                                { mode: "reset" },
                              )
                            }
                            onCompose={() =>
                              ensure.mutate({
                                entity_type: c.entity_type,
                                entity_id: c.entity_id,
                                title: c.name,
                              })
                            }
                          />
                        ))}
                      </div>
                    )}
                    {rest.length > 0 && filled.length > 0 && (
                      <p className="px-1 pt-4 pb-1 text-xs font-semibold tracking-wider text-faint uppercase">
                        Surfaced
                      </p>
                    )}
                    {rest.map((c) => (
                      <IdeaCard
                        key={`${c.entity_type}:${c.entity_id}`}
                        candidate={c}
                        active={sheetOpen && ideaCardMatchesTrail(c, trailRoot)}
                        busy={ensure.isPending}
                        onInspect={() =>
                          openDetail(
                            {
                              type: c.entity_type,
                              id: c.entity_id,
                              label: c.name,
                            },
                            { mode: "reset" },
                          )
                        }
                        onOpenSamples={() =>
                          openDetail(
                            {
                              type: c.entity_type,
                              id: c.entity_id,
                              label: c.name,
                            },
                            { mode: "reset", sectionFocus: "samples" },
                          )
                        }
                        onOpenArtist={(id, name) =>
                          openDetail(
                            { type: "artist", id, label: name },
                            { mode: "reset" },
                          )
                        }
                        onCompose={() =>
                          ensure.mutate({
                            entity_type: c.entity_type,
                            entity_id: c.entity_id,
                            title: c.name,
                          })
                        }
                      />
                    ))}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
